import React, { useState, useEffect, useMemo } from "react";
import api from "../utils/api";
import {
    FiX, FiSearch, FiDownload, FiFileText, FiCopy,
    FiChevronLeft, FiChevronRight
} from "react-icons/fi";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { format } from "date-fns";
import toast from "react-hot-toast";
import TicketDetails from "../components/TicketDetails";
import { useUser } from "../contexts/UserContext";
import { STATIC_PRIORITY_OPTIONS, STATIC_STATUS_OPTIONS } from "../pages/TicketListPage";

const AnalyticsDrilldownModal = ({ isOpen, onClose, title, type, month, employeeName, agingBucket, filters }) => {
    const { user } = useUser();
    const [loading, setLoading] = useState(false);
    const [tickets, setTickets] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedTicketId, setSelectedTicketId] = useState(null);

    useEffect(() => {
        if (isOpen) {
            setTickets([]);
            setSearchTerm("");
            setCurrentPage(1);
            fetchDrilldownData();
        }
    }, [isOpen, month, employeeName, agingBucket, type]);

    const fetchDrilldownData = async () => {
        setLoading(true);
        try {
            // Exclude `month` and `year` from the filters spread — they come from the
            // UI dropdowns (number or null) and would overwrite the specific chart-click
            // month string (e.g. "2026-04") causing all tickets to be returned.
            const { month: _fm, year: _fy, ...restFilters } = filters || {};

            const res = await api.get("/ticket/getAnalyticsDrilldown", {
                params: {
                    type,
                    month,
                    employeeName,
                    aging_bucket: agingBucket,
                    ...restFilters
                }
            });
            if (res.data?.status || res.data?.tickets || Array.isArray(res.data?.data)) {
                setTickets(res.data.data || res.data.tickets || []);
            }
        } catch (err) {
            console.error("Error fetching drilldown data:", err);
        } finally {
            setLoading(false);
        }
    };

    const exportDrilldownToExcel = async (reportData, title) => {
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet('Detail Report');
            const isTestingReject = type === "reject";
            const isClosure = type === "closure";

            const columns = [
                { header: 'S.No', key: 'sno', width: 6 },
                { header: 'Ticket ID', key: 'id', width: 12 },
                { header: 'Title', key: 'title', width: 55 },
                { header: 'Origin', key: 'origin', width: 12 },
                { header: 'Customer', key: 'customer_code', width: 15 },
                { header: 'Status', key: 'status', width: 15 },
                ...(isClosure ? [] : [{ header: isTestingReject ? 'Developer' : 'Assigned To', key: 'currently_working', width: 22 }]),
                { header: 'Aging', key: 'aging_display', width: 12 },
                { header: isTestingReject ? 'Tested By' : 'All Collaborators', key: 'collaborators', width: 30 },
                { header: 'Created At', key: 'created_date', width: 22 },
                { header: 'Last Updated', key: 'updated_date', width: 22 }
            ];

            const totalCols = columns.length;
            const colRange = String.fromCharCode(64 + totalCols);

            // Row 1: Company Branding
            worksheet.mergeCells(`A1:${colRange}1`);
            const companyRow = worksheet.getRow(1);
            companyRow.getCell(1).value = "eNova Software and Hardware Solutions P Ltd.";
            companyRow.getCell(1).font = { name: 'Arial', size: 14, bold: true, color: { argb: 'FFFF0000' } };
            companyRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            companyRow.height = 35;

            // Row 2: Report Title & Date
            const now = format(new Date(), 'dd MMM yyyy HH:mm');
            worksheet.mergeCells(`A2:${colRange}2`);
            const reportTitleRow = worksheet.getRow(2);
            reportTitleRow.getCell(1).value = `${title} (Generated: ${now})`;
            reportTitleRow.getCell(1).font = { name: 'Arial', size: 11, italic: true, color: { argb: 'FF444444' } };
            reportTitleRow.getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };
            reportTitleRow.height = 25;

            // Row 3: Table Headers
            const headerRow = worksheet.getRow(3);
            columns.forEach((col, idx) => {
                const cell = headerRow.getCell(idx + 1);
                cell.value = col.header;
                cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
                cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF666666' } };
                cell.alignment = { horizontal: 'center', vertical: 'middle' };
                cell.border = {
                    top: { style: 'thin' }, left: { style: 'thin' },
                    bottom: { style: 'thin' }, right: { style: 'thin' }
                };
            });
            headerRow.height = 25;

            // Set column widths properly
            columns.forEach((col, i) => {
                const actualWidth = (col.key === 'title') ? 75 : (col.key === 'collaborators') ? 40 : col.width;
                worksheet.getColumn(i + 1).width = actualWidth;
            });

            // Data Rows
            reportData.forEach((t, i) => {
                const rowValues = [];
                columns.forEach(col => {
                    if (col.key === 'sno') rowValues.push(i + 1);
                    else if (col.key === 'id') rowValues.push(`#${t.id}`);
                    else if (col.key === 'title') rowValues.push(t.title || "");
                    else if (col.key === 'origin') rowValues.push(((t.customer_name || "").toUpperCase() !== "INTERNAL") ? "External" : "Internal");
                    else if (col.key === 'customer_code') rowValues.push(t.customer_code || t.customer_name || "—");
                    else if (col.key === 'status') rowValues.push(t.status || "");
                    else if (col.key === 'currently_working') rowValues.push(t.assignee_name || t.assign_name || "—");
                    else if (col.key === 'aging_display') rowValues.push(t.total_days ? `${t.total_days}d` : "—");
                    else if (col.key === 'collaborators') rowValues.push(getCollaborators(t));
                    else if (col.key === 'created_date') rowValues.push(t.created_date ? format(new Date(t.created_date), 'dd/MM/yyyy HH:mm:ss') : "—");
                    else if (col.key === 'updated_date') rowValues.push(t.updated_date ? format(new Date(t.updated_date), 'dd/MM/yyyy HH:mm:ss') : "—");
                });

                const dataRow = worksheet.addRow(rowValues);
                dataRow.height = 22;
                dataRow.eachCell((cell, colIndex) => {
                    cell.border = {
                        top: { style: 'thin' }, left: { style: 'thin' },
                        bottom: { style: 'thin' }, right: { style: 'thin' }
                    };
                    const colKey = columns[colIndex - 1]?.key;
                    const isTitle = colKey === 'title';
                    const isCollab = colKey === 'collaborators';
                    cell.alignment = {
                        vertical: 'middle',
                        horizontal: (isTitle || isCollab) ? 'left' : 'center',
                        wrapText: (isTitle || isCollab)
                    };
                    if (colKey === 'id') {
                        cell.font = { color: { argb: 'FFEF4444' }, bold: true };
                    }
                });
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = url;
            anchor.download = `Report_${title.replace(/\s+/g, '_')}_${format(new Date(), 'yyyyMMdd_HHmm')}.xlsx`;
            anchor.click();
            toast.success('Excel Report Downloaded');
        } catch (error) {
            console.error('Excel export failed:', error);
            toast.error('Failed to generate Excel export');
        }
    };

    const exportDrilldownToPDF = (reportData, title) => {
        const doc = new jsPDF('l', 'pt', 'a4');
        const isTestingReject = type === "reject";
        const isClosure = type === "closure";

        let headers = ['S.No', 'Ticket ID', 'Title', 'Origin', 'Status'];
        if (!isClosure) {
            headers.push(isTestingReject ? 'Developer' : 'Assigned To');
        }
        headers.push('Aging', 'Created');

        const head = [headers];
        const body = reportData.map((t, idx) => {
            let row = [
                idx + 1,
                `#${t.id}`,
                t.title,
                t.origin || 'N/A',
                t.status,
            ];
            if (!isClosure) {
                row.push(t.assignee_name || t.assign_name || 'N/A');
            }
            row.push(t.total_days ? `${t.total_days}d` : 'N/A');
            row.push(t.created_date ? new Date(t.created_date).toLocaleDateString("en-GB") : 'N/A');
            return row;
        });

        doc.setFontSize(18);
        doc.setTextColor(30, 41, 59);
        doc.text(title, 40, 45);

        const columnStyles = {
            0: { halign: 'center', cellWidth: 40 },
            1: { textColor: [239, 68, 68], fontStyle: 'bold', cellWidth: 60, halign: 'center' },
            2: { cellWidth: 'auto' },
            3: { halign: 'center', cellWidth: 60 },
            4: { halign: 'center', cellWidth: 80 },
        };
        if (!isClosure) {
            columnStyles[5] = { cellWidth: 100 };
            columnStyles[6] = { halign: 'center', cellWidth: 60 };
            columnStyles[7] = { halign: 'center', cellWidth: 80 };
        } else {
            columnStyles[5] = { halign: 'center', cellWidth: 60 };
            columnStyles[6] = { halign: 'center', cellWidth: 80 };
        }

        autoTable(doc, {
            head,
            body,
            startY: 65,
            theme: 'grid',
            headStyles: {
                fillColor: [30, 41, 59],
                textColor: [255, 255, 255],
                fontSize: 10,
                fontStyle: 'bold',
                halign: 'center',
                cellPadding: 10
            },
            bodyStyles: {
                fontSize: 9,
                cellPadding: 8,
                textColor: [51, 65, 85]
            },
            columnStyles,
            alternateRowStyles: { fillColor: [248, 250, 252] },
            margin: { left: 40, right: 40 }
        });

        const totalPages = doc.internal.getNumberOfPages();
        for (let i = 1; i <= totalPages; i++) {
            doc.setPage(i);
            doc.setFontSize(8);
            doc.setTextColor(148, 163, 184);
            const dateStr = new Date().toLocaleString("en-GB");
            doc.text(`Page ${i} of ${totalPages} | Generated on ${dateStr}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 20, { align: 'right' });
        }

        doc.save(`Report_${title.replace(/\s+/g, '_')}.pdf`);
    };

    const filteredTickets = useMemo(() => {
        if (!searchTerm.trim()) return tickets;
        const s = searchTerm.toLowerCase();
        return tickets.filter(t =>
            String(t.id).includes(s) ||
            (t.title || "").toLowerCase().includes(s) ||
            (t.customer_name || "").toLowerCase().includes(s) ||
            (t.status || "").toLowerCase().includes(s)
        );
    }, [tickets, searchTerm]);

    const totalPages = rowsPerPage === "all" ? 1 : Math.max(1, Math.ceil(filteredTickets.length / rowsPerPage));
    const paginatedTickets = useMemo(() => {
        if (rowsPerPage === "all") return filteredTickets;
        const start = (currentPage - 1) * rowsPerPage;
        return filteredTickets.slice(start, start + rowsPerPage);
    }, [filteredTickets, currentPage, rowsPerPage]);

    // Format datetime to two-line dd/MM/yyyy and HH:mm
    const fmtDT = (str) => {
        if (!str || str === "-") return { d: "-", t: "" };
        const dt = new Date(str);
        const d = dt.toLocaleDateString("en-GB").replace(/\//g, "/");
        const t = dt.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
        return { d, t };
    };

    // Build collaborator list for a ticket
    const getCollaborators = (ticket) => {
        if (!ticket.employees || ticket.employees.length === 0) return "-";
        return ticket.employees.map(e =>
            (e.first_name || e.last_name)
                ? `${e.first_name || ""} ${e.last_name || ""}`.trim()
                : (e.user_name || `User #${e.user_id}`)
        ).join(", ");
    };

    // Aging display: total_days field
    const getAging = (ticket) => {
        if (!ticket.total_days || ticket.total_days === "-") return null;
        const d = parseFloat(ticket.total_days);
        const color =
            d < 5 ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                : d < 15 ? "bg-orange-50 text-orange-600 border-orange-100"
                    : "bg-red-50 text-red-600 border-red-100";
        return { label: `${d}d`, color };
    };

    // Status dot color
    const statusDot = (status) => {
        const s = (status || "").toLowerCase();
        if (s === "closed") return "bg-emerald-500";
        if (s.includes("reject")) return "bg-red-500";
        if (s.includes("process")) return "bg-blue-500";
        if (s.includes("new")) return "bg-slate-400";
        if (s.includes("approved")) return "bg-purple-500";
        return "bg-gray-400";
    };

    if (!isOpen) return null;

    const pageNumbers = [];
    if (type === "closure") {
        let startPage = Math.max(1, currentPage - 1);
        let endPage = startPage + 3;
        if (endPage > totalPages) {
            endPage = totalPages;
            startPage = Math.max(1, endPage - 3);
        }
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(i);
        }
    } else {
        for (let i = 1; i <= Math.min(totalPages, 10); i++) pageNumbers.push(i);
    }

    return (
        <div className="fixed inset-0 z-[9999] flex flex-col overflow-hidden" style={{ background: "rgba(15,23,42,0.6)", backdropFilter: "blur(8px)" }}>
            <div className="flex-1 flex flex-col m-3 bg-white rounded-[28px] shadow-2xl overflow-hidden">

                {/* ─── TOP TOOLBAR ─── */}
                <div className="flex items-start justify-between gap-4 px-8 py-5 border-b border-slate-100">
                    <div>
                        <h2 className="text-xl font-black text-slate-800 tracking-tight leading-tight">
                            {title || `${type === "reject" ? "Testing Rejected" : "Repeat"} Tickets`}
                        </h2>
                        <p className="mt-1 text-[9.5px] font-black text-red-500 uppercase tracking-[0.12em]">
                            Note: Click on Ticket ID or Title to view full details in the dashboard.
                        </p>
                    </div>

                    <div className="flex items-center gap-3 shrink-0">
                        {/* Rows per page */}
                        <div className="flex items-center gap-2">
                            <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">Rows</span>
                            <select
                                value={rowsPerPage}
                                onChange={e => { setRowsPerPage(e.target.value === "all" ? "all" : Number(e.target.value)); setCurrentPage(1); }}
                                className="bg-slate-50 border border-slate-200 rounded-xl px-3 py-2 text-xs font-bold text-slate-700 outline-none cursor-pointer hover:bg-white transition"
                            >
                                {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                                <option value="all">All</option>
                            </select>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                            <input
                                type="text"
                                placeholder="Search tickets..."
                                value={searchTerm}
                                onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                                className="w-64 pl-9 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-medium text-slate-700 placeholder:text-slate-400 outline-none focus:ring-4 focus:ring-blue-50 focus:bg-white transition"
                            />
                        </div>

                        {/* Export icons */}
                        <button
                            onClick={() => exportDrilldownToExcel(filteredTickets, title || `${type === "reject" ? "Testing Rejected" : "Repeat"} Tickets`)}
                            className="p-2.5 bg-slate-50 border border-slate-200 text-emerald-600 hover:bg-white rounded-xl transition active:scale-90"
                            title="Export Excel"
                        >
                            <FiDownload size={16} />
                        </button>
                        <button
                            onClick={() => exportDrilldownToPDF(filteredTickets, title || `${type === "reject" ? "Testing Rejected" : "Repeat"} Tickets`)}
                            className="p-2.5 bg-slate-50 border border-slate-200 text-red-500 hover:bg-white rounded-xl transition active:scale-90"
                            title="Export PDF"
                        >
                            <FiFileText size={16} />
                        </button>

                        {/* Close */}
                        <button
                            onClick={onClose}
                            className="p-2.5 bg-white border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 rounded-xl shadow-sm transition active:scale-90 ml-1"
                        >
                            <FiX size={18} />
                        </button>
                    </div>
                </div>

                {/* ─── TABLE ─── */}
                <div className="flex-1 overflow-auto custom-scroll">
                    {loading ? (
                        <div className="flex flex-col items-center justify-center h-full gap-4 py-32">
                            <div className="w-12 h-12 rounded-full border-4 border-slate-100 border-t-blue-600 animate-spin" />
                            <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">Loading tickets...</p>
                        </div>
                    ) : filteredTickets.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full py-32 text-slate-300">
                            <p className="text-sm font-black uppercase tracking-widest opacity-40">No tickets found</p>
                        </div>
                    ) : (
                        <table className="min-w-full border-separate border-spacing-0 text-left">
                            <thead className="sticky top-0 z-10">
                                <tr className="bg-white border-b border-slate-100">
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">S.No</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">ID ↕</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em]">Title</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] text-center whitespace-nowrap">Origin</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] text-center whitespace-nowrap">Customer</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">Status</th>
                                    {type !== "closure" && (
                                        <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">
                                            {type === "reject" ? "Developer" : "Assigned To"}
                                        </th>
                                    )}
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] text-center whitespace-nowrap">Aging</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] min-w-[160px]">
                                        {type === "reject" ? "Tested By" : "All Collaborators"}
                                    </th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">Created</th>
                                    <th className="px-5 py-4 text-[9px] font-black text-slate-400 uppercase tracking-[0.18em] whitespace-nowrap">Last Updated</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paginatedTickets.map((ticket, idx) => {
                                    const sno = rowsPerPage === "all" ? idx + 1 : (currentPage - 1) * rowsPerPage + idx + 1;
                                    const aging = getAging(ticket);
                                    const created = fmtDT(ticket.created_date);
                                    const updated = fmtDT(ticket.updated_date);
                                    const collaborators = getCollaborators(ticket);
                                    const isExternal = (ticket.customer_name || "").toUpperCase() !== "INTERNAL";

                                    return (
                                        <tr
                                            key={ticket.id}
                                            className="group border-b border-slate-50 hover:bg-blue-50/30 transition-colors"
                                        >
                                            <td className="px-5 py-4 text-xs font-bold text-slate-300">{sno}</td>

                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={(e) => {
                                                        if (e.ctrlKey || e.metaKey) {
                                                            window.open(`/tickets?tid=${ticket.id}`, "_blank");
                                                        } else {
                                                            setSelectedTicketId(ticket.id);
                                                        }
                                                    }}
                                                    className="text-[13px] font-black text-red-500 hover:underline transition"
                                                    title="Click to view details (Ctrl+Click to open in new tab)"
                                                >
                                                    #{ticket.id}
                                                </button>
                                            </td>

                                            <td className="px-5 py-4 max-w-[280px]">
                                                <div className="flex items-start gap-2">
                                                    <span
                                                        onClick={(e) => {
                                                            if (e.ctrlKey || e.metaKey) {
                                                                window.open(`/tickets?tid=${ticket.id}`, "_blank");
                                                            } else {
                                                                setSelectedTicketId(ticket.id);
                                                            }
                                                        }}
                                                        className="text-[12.5px] font-semibold text-slate-700 cursor-pointer hover:text-blue-600 transition leading-snug line-clamp-2"
                                                        title={ticket.title}
                                                    >
                                                        {ticket.title}
                                                    </span>
                                                    <FiCopy
                                                        size={11}
                                                        className="shrink-0 mt-0.5 text-slate-300 hover:text-slate-600 cursor-pointer opacity-0 group-hover:opacity-100 transition"
                                                        onClick={() => navigator.clipboard.writeText(ticket.title)}
                                                    />
                                                </div>
                                            </td>

                                            <td className="px-5 py-4 text-center">
                                                <span className={`px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-tight border ${isExternal
                                                        ? "bg-emerald-50 text-emerald-600 border-emerald-100"
                                                        : "bg-blue-50 text-blue-600 border-blue-100"
                                                    }`}>
                                                    {isExternal ? "External" : "Internal"}
                                                </span>
                                            </td>

                                            <td className="px-5 py-4 text-center">
                                                <span className="px-2.5 py-1 bg-slate-100 text-slate-500 rounded-lg text-[9px] font-black uppercase border border-slate-200/50">
                                                    {ticket.customer_code || ticket.customer_name || "—"}
                                                </span>
                                            </td>

                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${statusDot(ticket.status)}`} />
                                                    <span className="text-[11px] font-black text-slate-700 uppercase tracking-tight">
                                                        {ticket.status}
                                                    </span>
                                                </div>
                                            </td>

                                            {type !== "closure" && (
                                                <td className="px-5 py-4 whitespace-nowrap text-[12px] font-semibold text-slate-600">
                                                    {ticket.assignee_name || ticket.assign_name || "—"}
                                                </td>
                                            )}

                                            <td className="px-5 py-4 text-center">
                                                {aging ? (
                                                    <span className={`px-2.5 py-1 rounded-lg text-[10px] font-black border ${aging.color}`}>
                                                        {aging.label}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-300 text-[11px]">—</span>
                                                )}
                                            </td>

                                            <td className="px-5 py-4 text-[11px] text-slate-400 font-medium max-w-[200px] truncate" title={collaborators}>
                                                {collaborators}
                                            </td>

                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex flex-col leading-tight">
                                                    <span className="text-[11.5px] font-black text-slate-700">{created.d}</span>
                                                    <span className="text-[10px] font-semibold text-slate-400">{created.t}</span>
                                                </div>
                                            </td>

                                            <td className="px-5 py-4 whitespace-nowrap">
                                                <div className="flex flex-col leading-tight">
                                                    <span className="text-[11.5px] font-black text-slate-700">{updated.d}</span>
                                                    <span className="text-[10px] font-semibold text-slate-400">{updated.t}</span>
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>

                {/* ─── FOOTER PAGINATION ─── */}
                <div className="px-8 py-4 border-t border-slate-100 bg-white flex items-center justify-between">
                    <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-400">
                        Showing{" "}
                        <span className="text-slate-700">
                            {rowsPerPage === "all" ? 1 : Math.min((currentPage - 1) * rowsPerPage + 1, filteredTickets.length)}
                        </span>{" "}
                        to{" "}
                        <span className="text-slate-700">
                            {rowsPerPage === "all" ? filteredTickets.length : Math.min(currentPage * rowsPerPage, filteredTickets.length)}
                        </span>{" "}
                        of{" "}
                        <span className="text-slate-700 font-black">{filteredTickets.length}</span> results
                    </p>

                    <div className="flex items-center gap-1">
                        <button
                            disabled={currentPage === 1}
                            onClick={() => setCurrentPage(p => p - 1)}
                            className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                        >
                            <FiChevronLeft size={16} />
                        </button>
                        {pageNumbers.map(n => (
                            <button
                                key={n}
                                onClick={() => setCurrentPage(n)}
                                className={`w-9 h-9 rounded-xl text-[11px] font-black transition ${currentPage === n
                                        ? "bg-red-600 text-white shadow-lg shadow-red-200"
                                        : "bg-white border border-slate-200 text-slate-500 hover:bg-slate-50"
                                    }`}
                            >
                                {n}
                            </button>
                        ))}
                        <button
                            disabled={currentPage === totalPages}
                            onClick={() => setCurrentPage(p => p + 1)}
                            className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-700 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition"
                        >
                            <FiChevronRight size={16} />
                        </button>
                    </div>
                </div>
            </div>

            <style>{`
                .custom-scroll::-webkit-scrollbar { width: 5px; height: 5px; }
                .custom-scroll::-webkit-scrollbar-track { background: transparent; }
                .custom-scroll::-webkit-scrollbar-thumb { background: #e2e8f0; border-radius: 8px; }
                .custom-scroll::-webkit-scrollbar-thumb:hover { background: #cbd5e1; }
                .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
            `}</style>

            {selectedTicketId && (
                <div className="relative z-[500]">
                    <TicketDetails
                        ticketId={selectedTicketId}
                        tickets={tickets || []}
                        selectedTicketChildId="1"
                        priorityOptions={STATIC_PRIORITY_OPTIONS}
                        statusOptions={STATIC_STATUS_OPTIONS}
                        userInfo={user}
                        onClose={() => setSelectedTicketId(null)}
                        onSuccess={() => {
                            fetchDrilldownData();
                        }}
                    />
                </div>
            )}
        </div>
    );
};

export default AnalyticsDrilldownModal;
